import React, { useEffect, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import * as am5 from '@amcharts/amcharts5';
import * as am5map from '@amcharts/amcharts5/map';
import am5geodata from '@amcharts/amcharts5-geodata/worldLow';
import { ProbeRegionStatus } from '../../hooks/useProbeMetrics';

interface MonitorWorldMapProps {
  regions: ProbeRegionStatus[];
}

const REGION_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  // US
  'us-east-1':      { latitude: 39.0,   longitude: -77.5  },
  'us-east-2':      { latitude: 40.4,   longitude: -82.9  },
  'us-west-1':      { latitude: 37.8,   longitude: -122.4 },
  'us-west-2':      { latitude: 45.5,   longitude: -122.7 },
  // Canada
  'ca-central-1':   { latitude: 45.5,   longitude: -73.6  },
  'ca-west-1':      { latitude: 51.0,   longitude: -114.1 },
  // Europe
  'eu-west-1':      { latitude: 53.3,   longitude: -6.3   },
  'eu-west-2':      { latitude: 51.5,   longitude: -0.1   },
  'eu-west-3':      { latitude: 48.9,   longitude: 2.3    },
  'eu-central-1':   { latitude: 50.1,   longitude: 8.7    },
  'eu-central-2':   { latitude: 47.4,   longitude: 8.5    },
  'eu-north-1':     { latitude: 59.3,   longitude: 18.1   },
  'eu-south-1':     { latitude: 45.5,   longitude: 9.2    },
  'eu-south-2':     { latitude: 40.4,   longitude: -3.7   },
  // Asia Pacific
  'ap-northeast-1': { latitude: 35.7,   longitude: 139.7  },
  'ap-northeast-2': { latitude: 37.6,   longitude: 126.9  },
  'ap-northeast-3': { latitude: 34.7,   longitude: 135.5  },
  'ap-southeast-1': { latitude: 1.3,    longitude: 103.8  },
  'ap-southeast-2': { latitude: -33.9,  longitude: 151.2  },
  'ap-southeast-3': { latitude: -6.2,   longitude: 106.8  },
  'ap-southeast-4': { latitude: -37.8,  longitude: 144.9  },
  'ap-south-1':     { latitude: 19.1,   longitude: 72.9   },
  'ap-south-2':     { latitude: 17.4,   longitude: 78.5   },
  'ap-east-1':      { latitude: 22.3,   longitude: 114.2  },
  // South America
  'sa-east-1':      { latitude: -23.5,  longitude: -46.6  },
  // Middle East
  'me-south-1':     { latitude: 26.2,   longitude: 50.6   },
  'me-central-1':   { latitude: 24.5,   longitude: 54.4   },
  // Africa
  'af-south-1':     { latitude: -33.9,  longitude: 18.4   },
  // Israel
  'il-central-1':   { latitude: 32.1,   longitude: 34.8   },
};

const COLOR_SUCCESS = 0x10b981;
const COLOR_SLOW    = 0xf59e0b;
const COLOR_FAILURE = 0xef4444;
const SLOW_THRESHOLD_MS = 10_000;   // 10 s — must match MonitorRegionCards thresholds

type StatusLevel = 'success' | 'slow' | 'failure';

function statusLevel(r: ProbeRegionStatus): StatusLevel {
  // HTTP status code is ground truth: 4xx/5xx → failure, 2xx/3xx → success
  // (overrides the success boolean which can be stale from a previous Prometheus scrape)
  if (r.statusCode != null && r.statusCode >= 400) return 'failure';
  const effectiveSuccess = r.success || (r.statusCode != null && r.statusCode >= 200 && r.statusCode < 400);
  if (!effectiveSuccess) return 'failure';
  if (r.durationMs != null && r.durationMs >= SLOW_THRESHOLD_MS) return 'slow';
  return 'success';
}

const STATUS_COLOR: Record<StatusLevel, number> = {
  success: COLOR_SUCCESS,
  slow:    COLOR_SLOW,
  failure: COLOR_FAILURE,
};

const STATUS_META: Record<StatusLevel, { label: string; hex: string; bg: string; icon: string }> = {
  success: { label: 'Available', hex: '#10b981', bg: 'rgba(16,185,129,0.15)',  icon: '✓' },
  slow:    { label: 'Slow',      hex: '#f59e0b', bg: 'rgba(245,158,11,0.15)',  icon: '⚠' },
  failure: { label: 'Down',      hex: '#ef4444', bg: 'rgba(239,68,68,0.15)',   icon: '✗' },
};

// ── Tooltip HTML builder ────────────────────────────────────────────────────
function buildTooltipHTML(regs: ProbeRegionStatus[], isDark: boolean): string {
  const bg        = isDark ? '#1f2937' : '#ffffff';
  const border    = isDark ? '#374151' : '#e5e7eb';
  const shadow    = isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.12)';
  const ttText    = isDark ? '#f9fafb' : '#111827';
  const secondary = isDark ? '#9ca3af' : '#6b7280';
  const divColor  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const multiTarget = new Set(regs.map((r) => r.target)).size > 1;

  const rows = regs.map((r, i) => {
    const lvl = statusLevel(r);
    const sm  = STATUS_META[lvl];
    const dur = r.durationMs != null ? `${r.durationMs} ms` : '—';
    const checkedAt = new Date(r.checkedAt).toLocaleTimeString([], {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

    return `
      ${i > 0 ? `<div style="border-top:1px solid ${divColor};margin:10px 0 10px"></div>` : ''}
      ${multiTarget ? `<div style="font-size:10px;color:${secondary};font-family:monospace;margin-bottom:6px;word-break:break-all">${r.target}</div>` : ''}
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;margin-bottom:3px">
        <span style="font-size:12px;font-weight:700;color:${ttText};line-height:1.3">${r.regionLabel}</span>
        <span style="flex-shrink:0;font-size:10px;font-weight:600;padding:2px 8px;border-radius:12px;background:${sm.bg};color:${sm.hex};white-space:nowrap">${sm.icon}&nbsp;${sm.label}</span>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;font-size:11px;margin-bottom:4px">
        <span style="color:${secondary}">Response</span>
        <span style="font-weight:700;color:${sm.hex}">${dur}</span>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;font-size:11px;margin-bottom:5px">
        <span style="color:${secondary}">HTTP status</span>
        <span style="font-family:monospace;font-weight:600;color:${ttText}">${r.statusCode ?? '—'}</span>
      </div>
      <div style="font-size:10px;color:${secondary}">Last check: ${checkedAt}</div>
    `;
  }).join('');

  return `
    <div style="
      font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
      min-width:230px;max-width:290px;
      background:${bg};
      border:1px solid ${border};
      border-radius:8px;
      padding:12px 14px;
      box-shadow:0 6px 24px ${shadow};
    ">${rows}</div>`;
}

// ── Dot data per region ─────────────────────────────────────────────────────
interface DotEntry {
  latitude:    number;
  longitude:   number;
  dotColor:    number;
  tooltipHTML: string;
  isRecent:    boolean;
}

function buildDots(regions: ProbeRegionStatus[], isDark: boolean): DotEntry[] {
  // Aggregate multiple targets at the same AWS region into one dot
  const byRegion = new Map<string, ProbeRegionStatus[]>();
  for (const r of regions) {
    const arr = byRegion.get(r.region) ?? [];
    arr.push(r);
    byRegion.set(r.region, arr);
  }

  const dots: DotEntry[] = [];
  const now = Date.now();

  byRegion.forEach((regs, regionCode) => {
    const coords = REGION_COORDINATES[regionCode];
    if (!coords) return;

    const levels = regs.map(statusLevel);
    const worst: StatusLevel = levels.includes('failure') ? 'failure'
      : levels.includes('slow') ? 'slow' : 'success';

    const isRecent = regs.some(
      (r) => now - new Date(r.checkedAt).getTime() < 3 * 60 * 1000,
    );

    dots.push({
      latitude:    coords.latitude,
      longitude:   coords.longitude,
      dotColor:    STATUS_COLOR[worst],
      tooltipHTML: buildTooltipHTML(regs, isDark),
      isRecent,
    });
  });

  return dots;
}

// ── Update point series data ────────────────────────────────────────────────
function updateDots(pointSeries: any, dots: DotEntry[]) {
  pointSeries.data.setAll(
    dots.map((d) => ({
      latitude:    d.latitude,
      longitude:   d.longitude,
      dotColor:    d.dotColor,
      tooltipHTML: d.tooltipHTML,
      isRecent:    d.isRecent,
    })),
  );
}

// ── Component ───────────────────────────────────────────────────────────────
const MonitorWorldMap: React.FC<MonitorWorldMapProps> = ({ regions }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const chartRef       = useRef<HTMLDivElement | null>(null);
  const rootRef        = useRef<any>(null);
  const pointSeriesRef = useRef<any>(null);
  const am5Ref         = useRef<any>(null);
  const isDarkRef      = useRef(isDark);
  const regionsRef     = useRef(regions);

  // Rebuild dots on theme toggle (tooltip colours change)
  useEffect(() => {
    isDarkRef.current = isDark;
    if (pointSeriesRef.current) {
      updateDots(pointSeriesRef.current, buildDots(regionsRef.current, isDark));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark]);

  useEffect(() => {
    if (!chartRef.current) return;
    let cancelled = false;

    const initChart = () => {
      if (cancelled || !chartRef.current) return;

      am5Ref.current = am5;

      const root = am5.Root.new(chartRef.current);
      rootRef.current = root;
      if (root._logo) root._logo.dispose();

      const chart = root.container.children.push(
        am5map.MapChart.new(root, {
          panX:          'none',
          panY:          'none',
          wheelX:        'none',
          wheelY:        'none',
          projection:    am5map.geoMercator(),
          homeGeoPoint:  { longitude: 8, latitude: 20 },
          homeZoomLevel: 1.65,
          minZoomLevel:  1.65,
          maxZoomLevel:  1.65,
          paddingTop:    0,
          paddingBottom: 0,
          paddingLeft:   0,
          paddingRight:  0,
        })
      );
      chart.set('background', am5.Rectangle.new(root, { fillOpacity: 0 }));

      // ── Background polygon map (always dim, no status colouring) ──
      const idleFill     = isDarkRef.current ? 0x374151 : 0xd1d5db;
      const idleOp       = isDarkRef.current ? 0.5      : 0.75;
      const idleStroke   = isDarkRef.current ? 0x6b7280 : 0x9ca3af;
      const idleStrokeOp = isDarkRef.current ? 0.4      : 0.5;

      const polygonSeries = chart.series.push(
        am5map.MapPolygonSeries.new(root, { geoJSON: am5geodata })
      );
      polygonSeries.mapPolygons.template.setAll({
        fill:          am5.color(idleFill),
        fillOpacity:   idleOp,
        stroke:        am5.color(idleStroke),
        strokeOpacity: idleStrokeOp,
        strokeWidth:   0.7,
        interactive:   false,
      });

      // ── Dot series ──
      const pointSeries = chart.series.push(am5map.MapPointSeries.new(root, {}));
      pointSeriesRef.current = pointSeries;

      // Transparent amCharts tooltip shell — HTML is built inline per dot
      const tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        paddingLeft:   0,
        paddingRight:  0,
        paddingTop:    0,
        paddingBottom: 0,
        background: am5.RoundedRectangle.new(root, {
          fillOpacity:   0,
          strokeOpacity: 0,
        }),
      });
      tooltip.label.setAll({ html: '<div></div>', fontSize: 12 });

      pointSeries.bullets.push((_root: any, _series: any, dataItem: any) => {
        const color    = dataItem.dataContext?.dotColor ?? COLOR_SUCCESS;
        const htmlTip  = dataItem.dataContext?.tooltipHTML ?? '';
        const isRecent = dataItem.dataContext?.isRecent   ?? false;

        const circle = am5.Circle.new(root, {
          radius:          8,
          fill:            am5.color(color),
          fillOpacity:     0.92,
          stroke:          am5.color(0xffffff),
          strokeOpacity:   0.55,
          strokeWidth:     1.5,
          cursorOverStyle: 'pointer',
          tooltipHTML:     htmlTip,
          tooltip:         am5.Tooltip.new(root, {
            getFillFromSprite: false,
            paddingLeft:   0,
            paddingRight:  0,
            paddingTop:    0,
            paddingBottom: 0,
            background: am5.RoundedRectangle.new(root, {
              fillOpacity:   0,
              strokeOpacity: 0,
            }),
          }),
          shadowColor:     am5.color(color),
          shadowBlur:      10,
          shadowOpacity:   0.5,
        });

        if (isRecent) {
          const ring = am5.Circle.new(root, {
            radius:       8,
            fillOpacity:  0,
            stroke:       am5.color(color),
            strokeWidth:  2,
          });
          ring.animate({ key: 'radius',  from: 8,   to: 22,  duration: 1800, loops: Infinity, easing: am5.ease.out(am5.ease.cubic) });
          ring.animate({ key: 'opacity', from: 0.8, to: 0,   duration: 1800, loops: Infinity, easing: am5.ease.out(am5.ease.cubic) });
          const container = am5.Container.new(root, {});
          container.children.push(ring);
          container.children.push(circle);
          return am5.Bullet.new(root, { sprite: container });
        }

        return am5.Bullet.new(root, { sprite: circle });
      });

      polygonSeries.events.once('datavalidated', () => {
        if (!cancelled) {
          updateDots(pointSeries, buildDots(regionsRef.current, isDarkRef.current));
          chart.goHome();
        }
      });

      // suppress unused-variable warning for tooltip
      void tooltip;
    };

    initChart();

    return () => {
      cancelled = true;
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
        pointSeriesRef.current = null;
        am5Ref.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    regionsRef.current = regions;
    if (pointSeriesRef.current) {
      updateDots(pointSeriesRef.current, buildDots(regions, isDarkRef.current));
    }
  }, [regions]);

  return (
    <Box sx={{
      position:  'relative',
      width:     '100%',
      height:    '100%',
      minHeight: 320,
      borderRadius: 'inherit',
      overflow:  'hidden',
    }}>
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
      {regions.length === 0 && (
        <Box
          sx={{
            position:       'absolute',
            inset:          0,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            pointerEvents:  'none',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No monitoring data yet
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MonitorWorldMap;
